import { QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  LaneFilterState,
  LaneSummary,
  ShipmentOrderLane,
  ShipmentOrderLaneDetail,
  LaneBerthReservation,
  LaneManualAssignment,
} from 'src/entities/ShipmentOrderLane';

// Query Keys
export const SHIPMENT_ORDER_LANES_QUERY_KEY = 'shipment-order-lanes';
export const SHIPMENT_ORDER_LANES_SUMMARY_QUERY_KEY = 'shipment-order-lanes-summary';
export const SHIPMENT_ORDER_LANE_DETAIL_QUERY_KEY = 'shipment-order-lane-detail';

// API Response Types
type ShipmentOrderLanesResponse = {
  lanes: ShipmentOrderLane[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
  };
};

type ShipmentOrderLanesSummaryResponse = {
  summary: LaneSummary;
};

type ShipmentOrderLaneDetailResponse = {
  lane: ShipmentOrderLaneDetail;
};

// Query Key Types
type ShipmentOrderLanesQueryKey = [
  typeof SHIPMENT_ORDER_LANES_QUERY_KEY,
  {
    loadingStartOn: string;
    loadingEndOn: string;
    planningStatus?: string;
    deliveryMethod?: string;
    berthReservation?: string;
    keyword?: string;
    page: number;
    per: number;
  }
];

type ShipmentOrderLanesSummaryQueryKey = [
  typeof SHIPMENT_ORDER_LANES_SUMMARY_QUERY_KEY,
  {
    loadingStartOn: string;
    loadingEndOn: string;
    planningStatus?: string;
    deliveryMethod?: string;
    berthReservation?: string;
    keyword?: string;
  }
];

type ShipmentOrderLaneDetailQueryKey = [
  typeof SHIPMENT_ORDER_LANE_DETAIL_QUERY_KEY,
  number | null
];

// API Functions

/**
 * レーン一覧を取得
 */
const fetchShipmentOrderLanes = async (
  context: QueryFunctionContext<ShipmentOrderLanesQueryKey>,
): Promise<ShipmentOrderLanesResponse> => {
  const [, params] = context.queryKey;
  const searchParams = new URLSearchParams();

  searchParams.set('loading_start_on', params.loadingStartOn);
  searchParams.set('loading_end_on', params.loadingEndOn);
  searchParams.set('page', String(params.page));
  searchParams.set('per', String(params.per));

  if (params.planningStatus) searchParams.set('planning_status', params.planningStatus);
  if (params.deliveryMethod) searchParams.set('delivery_method', params.deliveryMethod);
  if (params.berthReservation) searchParams.set('berth_reservation', params.berthReservation);
  if (params.keyword) searchParams.set('keyword', params.keyword);

  const { data } = await axios.get<ShipmentOrderLanesResponse>(
    `/api/v5/shipment_order_lanes?${searchParams.toString()}`
  );
  return data;
};

/**
 * レーンサマリーを取得
 */
const fetchShipmentOrderLanesSummary = async (
  context: QueryFunctionContext<ShipmentOrderLanesSummaryQueryKey>,
): Promise<LaneSummary> => {
  const [, params] = context.queryKey;
  const searchParams = new URLSearchParams();

  searchParams.set('loading_start_on', params.loadingStartOn);
  searchParams.set('loading_end_on', params.loadingEndOn);

  if (params.planningStatus) searchParams.set('planning_status', params.planningStatus);
  if (params.deliveryMethod) searchParams.set('delivery_method', params.deliveryMethod);
  if (params.berthReservation) searchParams.set('berth_reservation', params.berthReservation);
  if (params.keyword) searchParams.set('keyword', params.keyword);

  const { data } = await axios.get<ShipmentOrderLanesSummaryResponse>(
    `/api/v5/shipment_order_lanes/summary?${searchParams.toString()}`
  );
  return data.summary;
};

/**
 * レーン詳細を取得
 */
const fetchShipmentOrderLaneDetail = async (
  context: QueryFunctionContext<ShipmentOrderLaneDetailQueryKey>,
): Promise<ShipmentOrderLaneDetail> => {
  const [, id] = context.queryKey;
  const { data } = await axios.get<ShipmentOrderLaneDetailResponse>(
    `/api/v5/shipment_order_lanes/${id}`
  );
  return data.lane;
};

// Update Request Type
export type UpdateShipmentOrderLaneRequest = {
  id: number;
  requiredTruckCount?: number | null;
  berthReservations?: Omit<LaneBerthReservation, 'id'>[];
  manualAssignments?: LaneManualAssignment[];
};

/**
 * レーンを更新
 */
const updateShipmentOrderLane = async (
  request: UpdateShipmentOrderLaneRequest,
): Promise<ShipmentOrderLaneDetail> => {
  const { id, ...body } = request;

  // snake_case に変換
  const requestBody = {
    required_truck_count: body.requiredTruckCount,
    berth_reservations: body.berthReservations?.map((r) => ({
      kind: r.kind,
      reservation_number: r.reservationNumber,
      reserved_from: r.reservedFrom,
      reserved_to: r.reservedTo,
      carrier_company: r.carrierCompany,
      truck_number: r.truckNumber,
      driver_name: r.driverName,
      driver_contact: r.driverContact,
    })),
    manual_assignments: body.manualAssignments?.map((a, idx) => ({
      id: a.id,
      sequence_number: idx + 1,
      quantity: a.quantity,
      weight_kg: a.weightKg,
      volume_m3: a.volumeM3,
      carrier_company: a.carrierCompany,
      operating_carrier: a.operatingCarrier,
      driver_name: a.driverName,
      truck_number: a.truckNumber,
      hierarchy_level: a.hierarchyLevel,
    })),
  };

  const { data } = await axios.patch<ShipmentOrderLaneDetailResponse>(
    `/api/v5/shipment_order_lanes/${id}`,
    requestBody
  );
  return data.lane;
};

// Custom Hooks

/**
 * レーン一覧取得フック
 */
export const useQueryShipmentOrderLanes = (params: LaneFilterState) => {
  return useQuery<ShipmentOrderLanesResponse, Error>({
    queryKey: [
      SHIPMENT_ORDER_LANES_QUERY_KEY,
      {
        loadingStartOn: params.loadingStartOn,
        loadingEndOn: params.loadingEndOn,
        planningStatus: params.planningStatus,
        deliveryMethod: params.deliveryMethod,
        berthReservation: params.berthReservation,
        keyword: params.keyword,
        page: params.page,
        per: params.per,
      },
    ],
    queryFn: fetchShipmentOrderLanes,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * レーンサマリー取得フック
 */
export const useQueryShipmentOrderLanesSummary = (params: {
  loadingStartOn: string;
  loadingEndOn: string;
  planningStatus?: string;
  deliveryMethod?: string;
  berthReservation?: string;
  keyword?: string;
}) => {
  return useQuery<LaneSummary, Error>({
    queryKey: [
      SHIPMENT_ORDER_LANES_SUMMARY_QUERY_KEY,
      {
        loadingStartOn: params.loadingStartOn,
        loadingEndOn: params.loadingEndOn,
        planningStatus: params.planningStatus,
        deliveryMethod: params.deliveryMethod,
        berthReservation: params.berthReservation,
        keyword: params.keyword,
      },
    ],
    queryFn: fetchShipmentOrderLanesSummary,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * レーン詳細取得フック
 */
export const useQueryShipmentOrderLaneDetail = (id: number | null) => {
  return useQuery<ShipmentOrderLaneDetail, Error>({
    queryKey: [SHIPMENT_ORDER_LANE_DETAIL_QUERY_KEY, id],
    queryFn: fetchShipmentOrderLaneDetail,
    enabled: id !== null,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * レーン更新ミューテーションフック
 */
export const useMutationShipmentOrderLane = () => {
  const queryClient = useQueryClient();

  return useMutation<ShipmentOrderLaneDetail, Error, UpdateShipmentOrderLaneRequest>({
    mutationFn: updateShipmentOrderLane,
    onSuccess: (updatedLane) => {
      // 詳細キャッシュを更新
      queryClient.setQueryData(
        [SHIPMENT_ORDER_LANE_DETAIL_QUERY_KEY, updatedLane.id],
        updatedLane
      );

      // 一覧キャッシュの該当行を部分更新
      const lanesQueries = queryClient.getQueriesData<ShipmentOrderLanesResponse>({
        queryKey: [SHIPMENT_ORDER_LANES_QUERY_KEY],
      });

      lanesQueries.forEach(([queryKey, queryData]) => {
        if (queryData?.lanes) {
          const updatedLanes = queryData.lanes.map((lane) =>
            lane.id === updatedLane.id
              ? {
                  ...lane,
                  laneStatus: updatedLane.laneStatus,
                  requiredTruckCount: updatedLane.requiredTruckCount,
                  assignedTruckCount: updatedLane.assignedTruckCount,
                  remainingTruckCount: updatedLane.remainingTruckCount,
                  loadingLocation: updatedLane.loadingLocation,
                  unloadingLocation: updatedLane.unloadingLocation,
                  updatedBy: updatedLane.updatedBy,
                  updatedAt: updatedLane.updatedAt,
                }
              : lane
          );

          queryClient.setQueryData(queryKey, {
            ...queryData,
            lanes: updatedLanes,
          });
        }
      });

      // サマリーを再取得
      void queryClient.invalidateQueries({
        queryKey: [SHIPMENT_ORDER_LANES_SUMMARY_QUERY_KEY],
      });
    },
  });
};
